import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, ResolveFn, RouterStateSnapshot } from "@angular/router";
import { catchError, map, Observable, of } from "rxjs";
import { GigsumoConstants } from "../services/GigsumoConstants";
import { ApiService } from "../services/api.service";
import { BusinessDataCacheService } from "../services/business-data-cache.service";
import { BusinessProvider } from "./business-details-guard.ts.service";


export const adminResolver : ResolveFn<any> =
     (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
      const busProvider = inject(BusinessProvider);
      const cache = inject(BusinessDataCacheService);
      const params = route.queryParams;
      const business = busProvider.getBusiness();
      const userId = localStorage.getItem('userId');

      // Check cache first for admin data
      const adminCacheKey = BusinessDataCacheService.generateAdminKey(userId || '');
      const cachedAdminData = cache.get(adminCacheKey);

      if (cachedAdminData) {
        console.log("📦 Admin data returned from cache");
        return of(cachedAdminData);
      }

      if(business != null){
        const adminData = {
          ...business,
          businessUser: true,
          isSuperAdmin: business.role === "SUPERADMIN" ? true : false
        };

        // Cache the admin data
        cache.set(adminCacheKey, adminData);
        return of(adminData);
      }
      else{
        return of(params);
      }
 };


export const adminGuard : CanActivateFn =
(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const userId = localStorage.getItem('userId');
  const busProvider = inject(BusinessProvider);

  // If no userId, allow access but set business to null
  if (!userId) {
    busProvider.setBusiness(null);
    return true;
  }

  // Allow access for all users. If user has a business, set business data.
  // In-line validations within pages handle plan-specific restrictions.
  return getBusinessDetails(userId).pipe(
    map(x => {
        // If user has a business, set the business data
        if (x && x.code === GigsumoConstants.SUCESSCODE && x.data?.businessData) {
          let data = x.data.businessData;
          let o = {
            role : x.data.role ,
            id: data.businessId,
            businessEmail: data.businessEmail,
            businessName: data.businessName,
            bid: data.platformBusinesId,
            plan: data.subscriptionType,
            businessOwner: data.businessOwner,
            organisationId: data.organisationId
          };
          busProvider.setBusiness(o);
        } else {
          // No business - clear business provider but still allow access
          busProvider.setBusiness(null);
        }
        // Always allow access to user-management pages
        return true;
    }),
  );
}


function getBusinessDetails(id : string) : Observable<any> {
  const API = inject(ApiService);
  const cache = inject(BusinessDataCacheService);

  // Check cache first
  const cacheKey = BusinessDataCacheService.generateBusinessKey(id);
  const cached = cache.get(cacheKey);

  if (cached) {
    console.log("📦 Business details returned from cache in adminGuard");
    return of({
      code: GigsumoConstants.SUCESSCODE,
      data: {
        businessData: cached,
        role: (cached as any).role
      }
    });
  }

  // Fetch from API if not cached
  return API.query(`business/findUserRoleInBusiness/${id}`).pipe(
    map(response => {
      // Cache the response if successful
      if (response && response.code === GigsumoConstants.SUCESSCODE && response.data?.businessData) {
        const processedData = {
          ...response.data.businessData,
          role: response.data.role
        };
        cache.set(cacheKey, processedData);
      }
      return response;
    }),
    catchError(e => {
      // On error, return null response - guard will allow access with no business
      return of({ code: 'ERROR', data: null });
    })
  );
}

